/**
 * Scan filter registry + scan execution.
 *
 * Two source-of-truth lists (Stock + Fundamental) drive the Add-Filter modal,
 * the active-filter editor, and the actual scan engine.
 *
 * Each filter declares an `id`, `category`, `label`, `unit`, and a `field`
 * resolver that returns the symbol's current value for that filter. The scan
 * engine evaluates the active filters against every symbol in the universe
 * with the user-chosen operator and threshold.
 *
 * Persistence: saved queries live in localStorage under
 * `nigastock.scans.{name}` so they survive reload. The async I/O shape is
 * swappable for a `/v1/broker/scans` BAL endpoint without touching UI code.
 */
import {
  optionStatsFor, quoteDetailsFor, fundamentalsFor,
} from './symbol-data';

export type FilterCategory = 'Stock' | 'Fundamental';
export type FilterUnit = 'price' | 'pct' | 'ratio' | 'count' | 'currency';
export type Operator = '>' | '>=' | '<' | '<=' | '=' | '!=';

export interface FilterDef {
  id: string;
  label: string;
  category: FilterCategory;
  unit: FilterUnit;
  /** Returns the symbol's current value for this filter. mark = last price. */
  field: (symbol: string, mark: number) => number;
}

export interface ActiveFilter {
  id: string;
  op: Operator;
  value: number;
}

// ----- Stock Filters (29) -----
const STOCK: Array<{ id: string; label: string; unit: FilterUnit; field: FilterDef['field'] }> = [
  { id: 'ask', label: 'Ask', unit: 'price', field: (s, m) => quoteDetailsFor(s, m).ask },
  { id: 'askSize', label: 'Ask Size', unit: 'count', field: (s, m) => quoteDetailsFor(s, m).askSize },
  { id: 'backVol', label: 'Back Volatility', unit: 'pct', field: (s, m) => optionStatsFor(s, m).historicalVolatility * 0.95 },
  { id: 'beta', label: 'Beta', unit: 'ratio', field: (s, m) => fundamentalsFor(s, m).beta },
  { id: 'bid', label: 'Bid', unit: 'price', field: (s, m) => quoteDetailsFor(s, m).bid },
  { id: 'bidSize', label: 'Bid Size', unit: 'count', field: (s, m) => quoteDetailsFor(s, m).bidSize },
  { id: 'callVolIdx', label: 'Call Volume Index', unit: 'ratio', field: (s) => 0.4 + (hash(s, 'cvi') % 200) / 100 },
  { id: 'close', label: 'Close', unit: 'price', field: (s, m) => quoteDetailsFor(s, m).close },
  { id: 'eps', label: 'EPS', unit: 'price', field: (s, m) => fundamentalsFor(s, m).eps },
  { id: 'frontVol', label: 'Front Volatility', unit: 'pct', field: (s, m) => optionStatsFor(s, m).impliedVolatility },
  { id: 'high', label: 'High', unit: 'price', field: (s, m) => quoteDetailsFor(s, m).high },
  { id: 'last', label: 'Last', unit: 'price', field: (_s, m) => m },
  { id: 'lastSize', label: 'Last Size', unit: 'count', field: (s) => 50 + (hash(s, 'ls') % 950) },
  { id: 'low', label: 'Low', unit: 'price', field: (s, m) => quoteDetailsFor(s, m).low },
  { id: 'mark', label: 'Mark', unit: 'price', field: (_s, m) => m },
  { id: 'marketCap', label: 'Market Cap', unit: 'currency', field: (s, m) => fundamentalsFor(s, m).marketCap / 1e9 },
  { id: 'mmm', label: 'Market Maker Move', unit: 'price', field: (s, m) => m * (0.5 + (hash(s, 'mmm') % 50) / 100) / 100 },
  { id: 'netChg', label: 'Net Change', unit: 'price', field: (s, m) => +(m - quoteDetailsFor(s, m).prevClose).toFixed(2) },
  { id: 'open', label: 'Open', unit: 'price', field: (s, m) => quoteDetailsFor(s, m).open },
  { id: 'optVolIdx', label: 'Option Volume Index', unit: 'ratio', field: (s) => 0.3 + (hash(s, 'ovi') % 250) / 100 },
  { id: 'pe', label: 'PE', unit: 'ratio', field: (s, m) => fundamentalsFor(s, m).pe },
  { id: 'pctChg', label: 'Percent Change', unit: 'pct', field: (s, m) => ((m - quoteDetailsFor(s, m).prevClose) / quoteDetailsFor(s, m).prevClose) * 100 },
  { id: 'pcr', label: 'Put/Call Ratio', unit: 'ratio', field: (s, m) => optionStatsFor(s, m).putCallRatio },
  { id: 'putVolIdx', label: 'Put Volume Index', unit: 'ratio', field: (s) => 0.2 + (hash(s, 'pvi') % 200) / 100 },
  { id: 'volume', label: 'Volume', unit: 'count', field: (s, m) => quoteDetailsFor(s, m).volume / 1000 },
  { id: 'shares', label: 'Shares', unit: 'count', field: (s, m) => fundamentalsFor(s, m).sharesOutMillions },
  { id: 'volDiff', label: 'Volatility Difference', unit: 'pct', field: (s, m) => optionStatsFor(s, m).impliedVolatility - optionStatsFor(s, m).historicalVolatility },
  { id: 'volIdx', label: 'Volatility Index', unit: 'pct', field: (s, m) => optionStatsFor(s, m).impliedVolatility * 0.85 },
  { id: 'wbVol', label: 'Weighted Back Volatility', unit: 'pct', field: (s, m) => optionStatsFor(s, m).historicalVolatility * 0.9 },
  { id: 'yield', label: 'Yield', unit: 'pct', field: (s, m) => fundamentalsFor(s, m).divYieldPct },
];

// ----- Fundamental Filters (30) -----
const FUND: Array<{ id: string; label: string; unit: FilterUnit; field: FilterDef['field'] }> = [
  { id: 'bvps', label: 'Book Value Per Share', unit: 'price', field: (s, m) => fundamentalsFor(s, m).bookValue },
  { id: 'bvpsGr', label: 'Book Value Per Share Growth', unit: 'pct', field: (s) => -3 + (hash(s, 'bvg') % 30) },
  { id: 'cfps', label: 'Cash Flow Per Share', unit: 'price', field: (s, m) => +(m * 0.08).toFixed(2) },
  { id: 'currentRatio', label: 'Current Ratio', unit: 'ratio', field: (s) => 1 + (hash(s, 'cr') % 200) / 100 },
  { id: 'divPayout', label: 'Dividend Payout', unit: 'pct', field: (s) => 5 + (hash(s, 'dp') % 60) },
  { id: 'divPayoutPS', label: 'Dividend Payout Per Share', unit: 'price', field: (s, m) => +(m * 0.04).toFixed(2) },
  { id: 'divYield', label: 'Dividend Yield', unit: 'pct', field: (s, m) => fundamentalsFor(s, m).divYieldPct },
  { id: 'epsLatest', label: 'Earnings Per Share', unit: 'price', field: (s, m) => fundamentalsFor(s, m).eps },
  { id: 'epsTTM', label: 'Earnings Per Share - TTM', unit: 'price', field: (s, m) => fundamentalsFor(s, m).eps * 1.05 },
  { id: 'finLev', label: 'Financial Leverage (Assets/Equity)', unit: 'ratio', field: (s) => 2 + (hash(s, 'fl') % 80) / 10 },
  { id: 'fixedChg', label: 'Fixed Charge Coverage Ratio', unit: 'ratio', field: (s) => 1 + (hash(s, 'fcc') % 80) / 10 },
  { id: 'fcfps', label: 'Free Cash Flow Per Share', unit: 'price', field: (s, m) => +(m * 0.06).toFixed(2) },
  { id: 'gpMargin', label: 'Gross Profit Margin', unit: 'pct', field: (s) => 20 + (hash(s, 'gpm') % 60) },
  { id: 'rateEst', label: 'Interest Rate - Estimated', unit: 'pct', field: (s) => 8 + (hash(s, 'ire') % 12) },
  { id: 'invTurnover', label: 'Inventory Turnover', unit: 'ratio', field: (s) => 5 + (hash(s, 'it') % 80) },
  { id: 'ltd2cap', label: 'Long-term Debt to Capital', unit: 'pct', field: (s) => 10 + (hash(s, 'ltd') % 70) },
  { id: 'mcEquity', label: 'Market Cap / Common Equity', unit: 'ratio', field: (s) => 0.5 + (hash(s, 'mce') % 100) / 20 },
  { id: 'netIncome', label: 'Net Income', unit: 'currency', field: (s, m) => +(fundamentalsFor(s, m).marketCap * 0.05 / 1e9).toFixed(2) },
  { id: 'npMargin', label: 'Net Profit Margin', unit: 'pct', field: (s) => 5 + (hash(s, 'npm') % 25) },
  { id: 'opMargin', label: 'Operating Profit Margin', unit: 'pct', field: (s) => 8 + (hash(s, 'opm') % 30) },
  { id: 'pbv', label: 'Price / Book Value Ratio', unit: 'ratio', field: (s, m) => +(m / fundamentalsFor(s, m).bookValue).toFixed(2) },
  { id: 'pcf', label: 'Price / Cash Flow Ratio', unit: 'ratio', field: (s) => 5 + (hash(s, 'pcf') % 50) },
  { id: 'per', label: 'Price / Earnings Ratio', unit: 'ratio', field: (s, m) => fundamentalsFor(s, m).pe },
  { id: 'quick', label: 'Quick Ratio', unit: 'ratio', field: (s) => 0.5 + (hash(s, 'qr') % 200) / 100 },
  { id: 'roa', label: 'Return on Assets (ROA)', unit: 'pct', field: (s) => 1 + (hash(s, 'roa') % 15) },
  { id: 'roe', label: 'Return on Equity (ROE)', unit: 'pct', field: (s) => 5 + (hash(s, 'roe') % 30) },
  { id: 'revenue', label: 'Revenue', unit: 'currency', field: (s, m) => +(fundamentalsFor(s, m).marketCap * 0.4 / 1e9).toFixed(2) },
  { id: 'sps', label: 'Sales Per Share', unit: 'price', field: (s, m) => +(m * 1.5).toFixed(2) },
  { id: 'tax', label: 'Tax Rate', unit: 'pct', field: (s) => 10 + (hash(s, 'tr') % 25) },
  { id: 'taTurnover', label: 'Total Asset Turnover', unit: 'ratio', field: (s) => 0.3 + (hash(s, 'tat') % 200) / 100 },
];

export const FILTER_REGISTRY: FilterDef[] = [
  ...STOCK.map((f) => ({ ...f, category: 'Stock' as FilterCategory })),
  ...FUND.map((f)  => ({ ...f, category: 'Fundamental' as FilterCategory })),
];

export const FILTER_BY_ID: Record<string, FilterDef> = Object.fromEntries(FILTER_REGISTRY.map((f) => [f.id, f]));

export interface SymbolRow { symbol: string; mark: number; pctChange: number; }

/** Apply active filters to a universe and return the matching symbols + the
 * computed value for each active filter (so we can show a Results table). */
export function runScan(universe: SymbolRow[], filters: ActiveFilter[]): Array<{
  row: SymbolRow; values: Record<string, number>;
}> {
  const out: Array<{ row: SymbolRow; values: Record<string, number> }> = [];
  for (const r of universe) {
    let pass = true; const values: Record<string, number> = {};
    for (const f of filters) {
      const def = FILTER_BY_ID[f.id]; if (!def) continue;
      const v = def.field(r.symbol, r.mark); values[f.id] = +v.toFixed(4);
      if (!cmp(v, f.op, f.value)) { pass = false; break; }
    }
    if (pass) out.push({ row: r, values });
  }
  return out;
}

function cmp(a: number, op: Operator, b: number): boolean {
  switch (op) {
    case '>':  return a > b;
    case '>=': return a >= b;
    case '<':  return a < b;
    case '<=': return a <= b;
    case '=':  return Math.abs(a - b) < 1e-6;
    case '!=': return Math.abs(a - b) >= 1e-6;
  }
}

function hash(s: string, salt: string): number {
  let h = 5381; const all = s + '|' + salt;
  for (let i = 0; i < all.length; i++) h = ((h << 5) + h) + all.charCodeAt(i);
  return Math.abs(h);
}

// ===== Saved-query persistence =====
const KEY_PREFIX = 'nigastock.scans.';
const INDEX_KEY = 'nigastock.scans.__index';

export interface SavedQuery {
  name: string;
  filters: ActiveFilter[];
  scanIn: ScanUniverse;
  savedAt: string;
}
export type ScanUniverse = 'All Stocks' | 'Watchlist' | 'Sector';

export interface ScanIO {
  list(): Promise<string[]>;
  load(name: string): Promise<SavedQuery | undefined>;
  save(q: SavedQuery): Promise<void>;
  remove(name: string): Promise<void>;
}

export const localScanStore: ScanIO = {
  async list() {
    if (typeof localStorage === 'undefined') return [];
    try { const v = JSON.parse(localStorage.getItem(INDEX_KEY) ?? '[]'); return Array.isArray(v) ? v : []; }
    catch { return []; }
  },
  async load(name) {
    if (typeof localStorage === 'undefined') return undefined;
    const raw = localStorage.getItem(KEY_PREFIX + name);
    if (!raw) return undefined;
    try { return JSON.parse(raw) as SavedQuery; } catch { return undefined; }
  },
  async save(q) {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(KEY_PREFIX + q.name, JSON.stringify(q));
    const idx = await this.list();
    if (!idx.includes(q.name)) {
      idx.push(q.name);
      localStorage.setItem(INDEX_KEY, JSON.stringify(idx));
    }
  },
  async remove(name) {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(KEY_PREFIX + name);
    const idx = (await this.list()).filter((n) => n !== name);
    localStorage.setItem(INDEX_KEY, JSON.stringify(idx));
  },
};
